import { useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import { useMeetingStore } from '../../contexts/meetingStore';
import { getParticipantColor } from '../../utils/participantColors';

export default function SubtitlesPanel({ transcripts, onClose }) {
  const { participants, participant, activeSpeaker, localTranscripts, translationEnabled, sttActive, sttLoading, sttLoadingProgress } = useMeetingStore();
  const messagesEndRef = useRef(null);

  // Merge local and server transcripts, sorted by timestamp
  // When translation is enabled, server transcripts include translations
  // Local transcripts are always available for local user's speech
  const allTranscripts = [...(transcripts || []), ...(localTranscripts || [])]
    .sort((a, b) => a.timestamp - b.timestamp)
    // Remove duplicates (local and server might both have same utterance)
    .filter((item, index, self) => {
      // Keep all items that don't have a matching peerId+timestamp within 2 seconds
      return index === self.findIndex(t =>
        t.peerId === item.peerId && Math.abs(t.timestamp - item.timestamp) < 2000
      );
    });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allTranscripts]);

  // Get display name for a peer
  const getDisplayName = (peerId) => {
    if (peerId === 'local' || peerId === participant?.socketId) {
      return participant?.displayName || 'You';
    }
    const p = participants.find((p) => p.socketId === peerId);
    return p?.displayName || 'Unknown';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      sx={{
        width: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SubtitlesIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Live Transcription</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* STT Loading indicator */}
      {sttLoading && (
        <Box sx={{ p: 2, bgcolor: 'primary.dark', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
            Loading speech recognition model... {sttLoadingProgress}%
          </Typography>
          <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, mt: 1, height: 4 }}>
            <Box sx={{ width: `${sttLoadingProgress}%`, bgcolor: 'primary.light', borderRadius: 1, height: '100%', transition: 'width 0.3s' }} />
          </Box>
        </Box>
      )}

      {/* Transcripts */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {allTranscripts.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
            <SubtitlesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {sttActive
                ? 'Transcriptions will appear here when someone speaks'
                : sttLoading
                  ? 'Loading speech recognition...'
                  : 'Speech recognition not active'}
            </Typography>
          </Box>
        ) : (
          allTranscripts.map((utterance) => {
            const isLocal = utterance.peerId === participant?.socketId;
            const color = getParticipantColor(utterance.peerId, isLocal);
            const isActive = utterance.peerId === activeSpeaker && !utterance.isComplete;

            return (
              <Paper
                key={utterance.id}
                elevation={isActive ? 4 : 1}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: color.bg,
                  borderRadius: 2,
                  borderLeft: `4px solid ${color.border}`,
                  transition: 'all 0.3s ease',
                  transform: isActive ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isActive ? `0 0 15px ${color.border}40` : undefined,
                }}
              >
                {/* Header: Name + Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: color.border,
                        boxShadow: isActive ? `0 0 8px ${color.border}` : 'none',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: color.name, fontWeight: 600 }}
                    >
                      {getDisplayName(utterance.peerId)}
                    </Typography>
                    {isActive && (
                      <Typography
                        variant="caption"
                        sx={{ color: color.border, fontSize: '10px', ml: 0.5 }}
                      >
                        speaking...
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '10px' }}>
                    {formatTime(utterance.timestamp)}
                  </Typography>
                </Box>

                {/* Original text (from server) or text (from local STT) */}
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(0,0,0,0.87)', lineHeight: 1.4 }}
                >
                  {utterance.original || utterance.text}
                </Typography>

                {/* Translation */}
                {utterance.translation && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: '1px dashed rgba(0,0,0,0.15)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0,0,0,0.7)',
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.5,
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>🌐</span>
                      <span style={{ flex: 1 }}>{utterance.translation}</span>
                      {utterance.isComplete && (
                        <span style={{ color: '#4caf50', fontSize: '14px' }}>✓</span>
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Loading indicator */}
                {!utterance.isComplete && !utterance.translation && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(0,0,0,0.4)',
                      fontStyle: 'italic',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <span style={{ fontSize: '12px' }}>🌐</span>
                    translating...
                  </Typography>
                )}
              </Paper>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Footer info */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          {allTranscripts.length} messages • {sttActive ? 'Local STT active' : 'STT inactive'}
          {translationEnabled && ' • Translating'}
        </Typography>
      </Box>
    </Box>
  );
}
